import type { Identifier } from '@atlaskit/media-client';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { useMediaClient } from '@databases/media-client';
import { MediaFormat } from '@databases/model/field';
import { defaultStoreUtils } from '@databases/redux';
import {
  isTemplateImageCover,
  getTemplateImageIdentifier,
} from '@databases/templates/utils';
import { type DatabaseAttachment } from '@databases/types/database';
import { getMediaCollectionId } from '@databases/utils/media-client';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import React, { type FunctionComponent, Suspense, useEffect, useState } from 'react';
import { type DropzoneRef } from 'react-dropzone';

import { dynamicImport } from '@canvas/dynamic-import';

import { AttachmentCard } from './AttachmentCard';
import { useMediaApiFileType } from './attachments';

const MediaViewer = React.lazy(() =>
  dynamicImport(() => import('@atlaskit/media-viewer')).then(({ MediaViewer }) => ({
    default: MediaViewer,
  }))
);

const MEDIA_ATTACHMENT_SIZE = '156px';
const THUMBNAIL_ATTACHMENT_SIZE = '32px';

const ImageContainerStyles = xcss({
  cursor: 'zoom-in',
});

const UploadingSpinnerContainerStyles = xcss({
  width: '100%',
  height: '100%',
});

const AttachmentAndButtonContainerStyles = xcss({
  height: 'inherit',
  position: 'relative',
});

const ThumbnailPlaceholder = () => {
  return (
    <Box
      xcss={xcss({
        width: THUMBNAIL_ATTACHMENT_SIZE,
        height: THUMBNAIL_ATTACHMENT_SIZE,
      })}
    />
  );
};

const DefaultPlaceholder = () => {
  return (
    <Box
      xcss={xcss({
        width: MEDIA_ATTACHMENT_SIZE,
        height: MEDIA_ATTACHMENT_SIZE,
      })}
    />
  );
};

interface MediaViewerType {
  dropzoneRef?: React.RefObject<DropzoneRef>;
  contentId: string;
  value: (
    | DatabaseAttachment
    | File
    | {
        src: string;
      }
    | {
        fileId: string;
      }
    | undefined
  ) & {
    _id: string;
  };
  handleDelete?: () => void;
  showSpinner?: boolean;
  mediaFiles?: Identifier[];
  mediaFormat: MediaFormat;
  attachmentLocation?: 'edit' | 'view';
}

const getFileIdentifier = (fileId: string, mediaCollectionId: string): Identifier => {
  if (isTemplateImageCover(fileId)) {
    return getTemplateImageIdentifier(fileId);
  } else {
    return {
      mediaItemType: 'file',
      id: fileId,
      collectionName: mediaCollectionId,
    };
  }
};

const AttachmentViewer: FunctionComponent<MediaViewerType> = ({
  contentId,
  value,
  showSpinner,
  mediaFiles,
  mediaFormat,
  attachmentLocation,
}) => {
  const [mediaCollectionId, setMediaCollectionId] = useState<null | string>(null);
  const mediaClient = useMediaClient(contentId);
  const [file, setFile] = useState<null | Identifier>(null);

  const store = defaultStoreUtils.useStore();

  const isThumbnail = mediaFormat === MediaFormat.THUMBNAIL;

  const attachmentData = useMediaApiFileType(
    typeof value === 'object' && 'fileId' in value ? value : undefined,
    contentId
  );

  const handleViewInFullscreen = (e?: React.MouseEvent) => {
    if (!inEditMode && value && 'fileId' in value && mediaCollectionId) {
      if (e) {
        e.stopPropagation();
      }
      const fileData = getFileIdentifier(value.fileId, mediaCollectionId);
      setFile(fileData);
    }
  };

  useEffect(() => {
    const getCollectionId = async () => {
      const id = await getMediaCollectionId(store.getState(), contentId);
      if (id) {
        setMediaCollectionId(id);
      }
    };

    getCollectionId();
  }, [contentId, store]);

  if (showSpinner) {
    return (
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="center"
        xcss={UploadingSpinnerContainerStyles}
      >
        <Spinner />
      </Flex>
    );
  }

  if (attachmentData?.status === AsyncOperationStatus.PENDING) {
    if (isThumbnail) {
      return <ThumbnailPlaceholder />;
    }
    return <DefaultPlaceholder />;
  }

  if (attachmentData?.status !== AsyncOperationStatus.SUCCEEDED) {
    if (isThumbnail) {
      return <ThumbnailPlaceholder />;
    }
    return <DefaultPlaceholder />;
  }

  if (
    file &&
    mediaFiles &&
    mediaCollectionId &&
    mediaClient &&
    mediaClient.mediaClientConfig
  ) {
    return (
      <Suspense fallback={<Spinner />}>
        <MediaViewer
          mediaClientConfig={mediaClient.mediaClientConfig}
          selectedItem={file}
          items={mediaFiles}
          collectionName={mediaCollectionId}
          onClose={() => setFile(null)}
        />
      </Suspense>
    );
  }

  const shouldDisableOverlay =
    isThumbnail ||
    attachmentData.data === 'image' ||
    attachmentData.data === 'audio' ||
    attachmentData.data === 'video';

  const inEditMode = attachmentLocation === 'edit';

  return (
    attachmentData &&
    mediaClient &&
    mediaCollectionId &&
    'fileId' in value && (
      <Box
        testId="attachment-card"
        xcss={[AttachmentAndButtonContainerStyles, !inEditMode && ImageContainerStyles]}
        onClick={handleViewInFullscreen}
      >
        <AttachmentCard
          isThumbnail={isThumbnail}
          attachmentLocation={attachmentLocation}
          shouldDisableOverlay={shouldDisableOverlay}
          mediaClientConfig={mediaClient.mediaClientConfig}
          attachmentIdentifier={getFileIdentifier(value.fileId, mediaCollectionId)}
        />
      </Box>
    )
  );
};

export default AttachmentViewer;
